import { useEffect, useMemo, useState } from "react";
import BottomSheet from "./BottomSheet";
import { ALL_TRACKS } from "../lib/constants";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { AlertTriangle, ChevronDown, ChevronUp } from "lucide-react";

function nextDates(n) {
  const out = [];
  const today = new Date();
  today.setUTCHours(0, 0, 0, 0);
  for (let i = 0; i < n; i++) {
    const d = new Date(today);
    d.setUTCDate(today.getUTCDate() + i);
    out.push(d.toISOString().slice(0, 10));
  }
  return out;
}

function fmtDateChip(iso) {
  const d = new Date(`${iso}T00:00:00Z`);
  const weekday = d.toLocaleDateString(undefined, { weekday: "short", timeZone: "UTC" });
  const dm = d.toLocaleDateString(undefined, { day: "2-digit", month: "short", timeZone: "UTC" });
  return `${weekday} ${dm}`;
}

function fmtLocalTime(iso) {
  try { return new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false }); }
  catch { return ""; }
}

export default function LoadRaceCardModal({ open, onClose, group, onLoaded }) {
  const dateOptions = useMemo(() => nextDates(14), []);
  const [step, setStep] = useState(1);
  const [selDates, setSelDates] = useState([]);
  const [selTracks, setSelTracks] = useState([]);
  const [preview, setPreview] = useState({ races: [], warnings: [] });
  const [selRaces, setSelRaces] = useState({}); // { "date|track": Set<int> }
  const [expanded, setExpanded] = useState({}); // panel expand state
  const [previewing, setPreviewing] = useState(false);
  const [loading, setLoading] = useState(false);

  const allowedTracks = group?.allowed_tracks || ALL_TRACKS;
  const allowedRaceNums = group?.allowed_race_numbers || [];

  const reset = () => {
    setStep(1); setSelDates([]); setSelTracks([]);
    setPreview({ races: [], warnings: [] }); setSelRaces({}); setExpanded({});
  };
  const handleClose = () => { reset(); onClose(); };

  // Pre-check allowed tracks when first entering step 2
  useEffect(() => {
    if (open && step === 2 && selTracks.length === 0) {
      setSelTracks(ALL_TRACKS.filter((t) => allowedTracks.includes(t)).slice(0, 3));
    }
  }, [open, step, allowedTracks, selTracks.length]);

  const toggleDate = (d) => {
    setSelDates((prev) => prev.includes(d) ? prev.filter((x) => x !== d) : [...prev, d]);
  };
  const toggleTrack = (t) => {
    setSelTracks((prev) => prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]);
  };

  const runPreview = async () => {
    setPreviewing(true);
    try {
      const { data } = await api.post(`/groups/${group.id}/races/preview-card`, {
        dates: selDates, tracks: selTracks,
      });
      setPreview({ races: data.races, warnings: data.warnings || [] });
      // Auto-check allowed race numbers only
      const map = {};
      const exp = {};
      const allowedSet = new Set(allowedRaceNums);
      data.races.forEach((r) => {
        const key = `${r.meeting_date}|${r.track}`;
        map[key] = map[key] || new Set();
        if (allowedSet.has(r.race_number)) map[key].add(r.race_number);
        exp[key] = false;
      });
      setSelRaces(map);
      setExpanded(exp);
      setStep(3);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, "Preview failed"));
    } finally { setPreviewing(false); }
  };

  const toggleRace = (key, num) => {
    setSelRaces((prev) => {
      const next = { ...prev };
      const s = new Set(next[key] || []);
      if (s.has(num)) s.delete(num); else s.add(num);
      next[key] = s;
      return next;
    });
  };

  const toggleAllForKey = (key, races) => {
    setSelRaces((prev) => {
      const next = { ...prev };
      const cur = next[key] || new Set();
      const raceNums = races.map((r) => r.race_number);
      const allOn = raceNums.every((n) => cur.has(n));
      next[key] = allOn ? new Set() : new Set(raceNums);
      return next;
    });
  };

  const totalPicked = useMemo(() => {
    return Object.values(selRaces).reduce((sum, s) => sum + s.size, 0);
  }, [selRaces]);

  const outOfFilterCount = useMemo(() => {
    let n = 0;
    const allowedSet = new Set(allowedRaceNums);
    const allowedTrackSet = new Set(allowedTracks);
    Object.entries(selRaces).forEach(([key, set]) => {
      const [, track] = key.split("|");
      set.forEach((rn) => {
        if (!allowedTrackSet.has(track) || !allowedSet.has(rn)) n++;
      });
    });
    return n;
  }, [selRaces, allowedRaceNums, allowedTracks]);

  const doLoad = async () => {
    // Build races_per_track (aggregate across dates → track → union of numbers)
    const byTrack = new Map();
    Object.entries(selRaces).forEach(([key, set]) => {
      if (set.size === 0) return;
      const [, track] = key.split("|");
      const cur = byTrack.get(track) || new Set();
      set.forEach((n) => cur.add(n));
      byTrack.set(track, cur);
    });
    const races_per_track = [...byTrack.entries()].map(([track, s]) => ({
      track, race_numbers: [...s].sort((a, b) => a - b),
    }));
    if (totalPicked === 0) { toast.error("Select at least one race"); return; }

    setLoading(true);
    try {
      const { data } = await api.post(`/groups/${group.id}/races/load-card`, {
        dates: selDates, tracks: selTracks, races_per_track,
      });
      toast.success(`Loaded ${data.loaded} race${data.loaded === 1 ? "" : "s"}` +
        (data.skipped_duplicates ? ` (skipped ${data.skipped_duplicates} duplicate)` : ""));
      onLoaded && onLoaded();
      handleClose();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail, "Load failed"));
    } finally { setLoading(false); }
  };

  // Group preview by date|track
  const previewGrouped = useMemo(() => {
    const grouped = {};
    preview.races.forEach((r) => {
      const key = `${r.meeting_date}|${r.track}`;
      grouped[key] = grouped[key] || [];
      grouped[key].push(r);
    });
    return grouped;
  }, [preview]);

  return (
    <BottomSheet open={open} onClose={handleClose} title="Load racing card" testId="load-card-modal">
      {/* Step indicator */}
      <div className="flex gap-2 mb-4" data-testid="load-card-steps">
        {[1, 2, 3].map((s) => (
          <div key={s} className={`flex-1 h-1.5 rounded-full ${step >= s ? "bg-[#1971c2]" : "bg-[#e9ecef]"}`} />
        ))}
      </div>

      {step === 1 && (
        <div className="space-y-4">
          <label className="block text-xs font-bold uppercase tracking-wider text-[#6c757d]">
            Step 1 · Pick meeting dates
          </label>
          <div className="grid grid-cols-2 gap-2" data-testid="load-card-dates">
            {dateOptions.map((d) => {
              const active = selDates.includes(d);
              return (
                <button
                  key={d}
                  onClick={() => toggleDate(d)}
                  data-testid={`load-card-date-${d}`}
                  className={`rounded-xl px-3 py-2.5 text-xs font-bold border transition-transform active:scale-95 ${
                    active ? "bg-[#1971c2] text-white border-[#1971c2]" : "bg-white text-[#212529] border-[#e9ecef]"
                  }`}
                >
                  {fmtDateChip(d)}
                </button>
              );
            })}
          </div>
          <button
            disabled={selDates.length === 0}
            onClick={() => setStep(2)}
            data-testid="load-card-next-1"
            className="w-full bg-[#1971c2] hover:bg-[#1864ab] text-white font-display font-extrabold text-base rounded-xl py-3 active:scale-95 transition-transform disabled:opacity-40"
          >
            Next — pick tracks
          </button>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-4">
          <label className="block text-xs font-bold uppercase tracking-wider text-[#6c757d]">
            Step 2 · Pick tracks
          </label>
          <div className="grid grid-cols-2 gap-2" data-testid="load-card-tracks">
            {ALL_TRACKS.map((t) => {
              const active = selTracks.includes(t);
              const outside = !allowedTracks.includes(t);
              return (
                <button
                  key={t}
                  onClick={() => toggleTrack(t)}
                  data-testid={`load-card-track-${t}`}
                  className={`rounded-xl px-3 py-2.5 text-xs font-bold border transition-transform active:scale-95 text-left ${
                    active ? "bg-[#1971c2] text-white border-[#1971c2]" :
                    outside ? "bg-[#fff4e6] text-[#c07800] border-[#c07800]/25" :
                    "bg-white text-[#212529] border-[#e9ecef]"
                  }`}
                >
                  {t}
                </button>
              );
            })}
          </div>
          <div className="flex gap-2">
            <button onClick={() => setStep(1)} className="flex-1 bg-white border-2 border-[#e9ecef] font-bold rounded-xl py-3">Back</button>
            <button
              disabled={selTracks.length === 0 || previewing}
              onClick={runPreview}
              data-testid="load-card-next-2"
              className="flex-1 bg-[#1971c2] hover:bg-[#1864ab] text-white font-display font-extrabold rounded-xl py-3 active:scale-95 disabled:opacity-40"
            >
              {previewing ? "Loading…" : "Preview races"}
            </button>
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="space-y-3">
          <label className="block text-xs font-bold uppercase tracking-wider text-[#6c757d]">
            Step 3 · Choose races to load
          </label>

          {(preview.warnings?.length > 0 || outOfFilterCount > 0) && (
            <div className="rounded-xl bg-[#fff4e6] border border-[#c07800]/25 p-3 flex gap-2 text-[11px] text-[#c07800] font-semibold" data-testid="load-card-warning">
              <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" strokeWidth={2.5} />
              <div>
                {preview.warnings.map((w, i) => <div key={i}>{w}</div>)}
                {outOfFilterCount > 0 && (
                  <div>{outOfFilterCount} race{outOfFilterCount === 1 ? "" : "s"} selected outside your competition filter — you can still load them.</div>
                )}
              </div>
            </div>
          )}

          <div className="space-y-2 max-h-[45vh] overflow-y-auto" data-testid="load-card-preview">
            {Object.keys(previewGrouped).sort().map((key) => {
              const races = previewGrouped[key];
              const [date, track] = key.split("|");
              const selSet = selRaces[key] || new Set();
              const isOpen = expanded[key];
              const allOn = races.every((r) => selSet.has(r.race_number));
              return (
                <div key={key} className="border border-[#e9ecef] rounded-xl overflow-hidden">
                  <div className="flex items-center justify-between px-3 py-2 bg-[#f8f9fa]">
                    <button
                      onClick={() => setExpanded((prev) => ({ ...prev, [key]: !prev[key] }))}
                      className="flex items-center gap-1.5 text-xs font-bold text-left flex-1 min-w-0"
                      data-testid={`load-card-panel-${date}-${track.replace(/\s+/g, "_")}`}
                    >
                      {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      <span className="truncate">{fmtDateChip(date)} · {track}</span>
                      <span className="text-[10px] text-[#6c757d] font-bold ml-1">{selSet.size}/{races.length}</span>
                    </button>
                    <button
                      onClick={() => toggleAllForKey(key, races)}
                      className="text-[10px] font-bold text-[#1971c2] hover:underline"
                    >
                      {allOn ? "Clear" : "Select all"}
                    </button>
                  </div>
                  {isOpen && (
                    <div className="p-2 space-y-1">
                      {races.map((r) => {
                        const on = selSet.has(r.race_number);
                        const outsideFilter = !allowedRaceNums.includes(r.race_number);
                        return (
                          <label key={r.race_number} className={`flex items-center gap-2 rounded-lg px-2 py-1.5 cursor-pointer text-xs ${on ? "bg-[#e7f0fb]" : "hover:bg-[#f8f9fa]"}`}>
                            <input
                              type="checkbox"
                              checked={on}
                              onChange={() => toggleRace(key, r.race_number)}
                              className="w-4 h-4 accent-[#1971c2]"
                              data-testid={`race-check-${date}-${track.replace(/\s+/g, "_")}-${r.race_number}`}
                            />
                            <span className="font-bold w-8">R{r.race_number}</span>
                            <span className="flex-1 truncate">{r.race_name}</span>
                            <span className="text-[#6c757d] tabular-nums">{fmtLocalTime(r.scheduled_time)}</span>
                            {outsideFilter && (
                              <span className="text-[8px] font-black uppercase tracking-wider bg-[#c07800]/10 text-[#c07800] rounded-full px-1.5 py-0.5">Off</span>
                            )}
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <div className="flex gap-2 pt-1">
            <button onClick={() => setStep(2)} className="flex-1 bg-white border-2 border-[#e9ecef] font-bold rounded-xl py-3">Back</button>
            <button
              disabled={totalPicked === 0 || loading}
              onClick={doLoad}
              data-testid="load-card-submit"
              className="flex-1 bg-[#1971c2] hover:bg-[#1864ab] text-white font-display font-extrabold rounded-xl py-3 active:scale-95 disabled:opacity-40"
            >
              {loading ? "Loading…" : `Load ${totalPicked} race${totalPicked === 1 ? "" : "s"}`}
            </button>
          </div>
        </div>
      )}
    </BottomSheet>
  );
}
